import { useEffect } from "react";
import { Form, Input, Button, Row, Col, Tooltip } from "antd";
import { useDispatch } from "react-redux";
import { updateAccountInfo } from "../../actionCreator/commonActionCreator";
import { getUserInfo } from "../../utils/utils";
import {
  formatAndExtractCountryCode,
  handleMobileChange,
  normalizeMobileNumber,
  prefixSelector,
  validateMobileNumber,
} from "../../utils/mobileValidation";
import PlaceAutocomplete from "../../common/PlaceAutocomplete";
import { getToken } from "../../utils/auth";
import { useAlert } from "../../context/AlertProvider";
import { countries } from "../../constant/Countries";

const Info = () => {
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const { showAlert } = useAlert();
  const userAccountInfo = getUserInfo();

  const onInitialValue = () => {
    const formInitialValues = {
      prefix:
        formatAndExtractCountryCode(userAccountInfo?.account?.mobileNo)
          ?.countryLabel || "India",
      phPrefix:
        formatAndExtractCountryCode(userAccountInfo?.account?.phoneNo)
          ?.countryLabel || "India",
      firstname: userAccountInfo?.firstName || "",
      lastname: userAccountInfo?.lastName || "",
      title: userAccountInfo?.account?.designation || "",
      phoneNo:
        formatAndExtractCountryCode(userAccountInfo?.account?.phoneNo)
          ?.numberWithoutCountryCode || "",
      mobileNo:
        formatAndExtractCountryCode(userAccountInfo?.account?.mobileNo)
          ?.numberWithoutCountryCode || "",
      email: userAccountInfo?.email || "",
      company: userAccountInfo?.companyName || "",
    };
    form.setFieldsValue(formInitialValues);
  };

  useEffect(() => {
    onInitialValue();
  }, [form]);

  const onFinish = async (values) => {
    const payload = {
      firstName: values.firstname,
      lastName: values.lastname,
      companyName: values.company,
      account: {
        mobileNo: values?.mobileNo
          ? countries?.find((d) => d?.label === values?.prefix)?.code
            ? "+" +
              countries?.find((d) => d?.label === values?.prefix)?.code +
              " " +
              values?.mobileNo
            : values?.mobileNo
          : undefined,
        phoneNo: values?.phoneNo
          ? countries?.find((d) => d?.label === values?.phPrefix)?.code
            ? "+" +
              countries?.find((d) => d?.label === values?.phPrefix)?.code +
              " " +
              values?.phoneNo
            : values?.phoneNo
          : undefined,
        designation: values.title,
      },
    };

    const res = await dispatch(updateAccountInfo(getToken(), payload));
    if (res?.status === 200) {
      showAlert("success", "Information has been updated successfully.");
    } else {
      showAlert("error", res?.response?.data?.title);
    }
  };

  return (
    <div style={{ marginLeft: 25 }} className="errorformcontainer mt-3">
      <Form form={form} layout="vertical" onFinish={onFinish}>
        <Row className="justify-content-between">
          <Col xs={24} md={6}>
            <label>
              First Name<span className="text-error">*</span>
            </label>
          </Col>
          <Col xs={24} md={17}>
            <Form.Item
              name="firstname"
              className="mb15"
              rules={[{ required: true, message: "Please enter first name" }]}
            >
              <Input className="common-input" />
            </Form.Item>
          </Col>
        </Row>

        <Row className="justify-content-between">
          <Col xs={24} md={6}>
            <label>
              Last Name<span className="text-error">*</span>
            </label>
          </Col>
          <Col xs={24} md={17}>
            <Form.Item
              name="lastname"
              className="mb15"
              rules={[{ required: true, message: "Please enter last name" }]}
            >
              <Input className="common-input" />
            </Form.Item>
          </Col>
        </Row>

        <Row className="justify-content-between">
          <Col xs={24} md={6}>
            <label>Designation</label>
          </Col>
          <Col xs={24} md={17}>
            <Form.Item className="mb15" name="title">
              <Input className="common-input" />
            </Form.Item>
          </Col>
        </Row>
        <Row className="justify-content-between">
          <Col xs={24} md={6}>
            <label>Phone</label>
          </Col>
          <Col xs={24} md={17}>
            <Form.Item
              className="mb15"
              name="phoneNo"
              normalize={normalizeMobileNumber}
              rules={[
                {
                  validator: validateMobileNumber,
                },
              ]}
            >
              <Input
                className="common-input"
                addonBefore={prefixSelector("phPrefix")}
                maxLength={20}
                onKeyDown={(e) => {
                  if (e.key === "+") {
                    e.preventDefault();
                  }
                }}
                onChange={(e) => handleMobileChange(e.target.value, form)}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row className="justify-content-between">
          <Col xs={24} md={6}>
            <label>
              Mobile<span className="text-error">*</span>
            </label>
          </Col>
          <Col xs={24} md={17}>
            <Form.Item
              name="mobileNo"
              className="mb15"
              onKeyDown={(e) => {
                if (e.key === "+") {
                  e.preventDefault();
                }
              }}
              normalize={normalizeMobileNumber}
              rules={[
                {
                  required: true,
                  message: "Please enter mobile number",
                },
                {
                  validator: validateMobileNumber,
                },
              ]}
            >
              <Input
                className="common-input mobile-no"
                addonBefore={prefixSelector()}
                type="number"
                maxLength={20}
                onChange={(e) => handleMobileChange(e.target.value, form)}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row className="justify-content-between">
          <Col xs={24} md={6}>
            <label>
              Email<span className="text-error">*</span>
            </label>
          </Col>
          <Col xs={24} md={17}>
            <Tooltip title="Email update is not allowed!">
              <Form.Item name="email" className="mb15">
                <Input readOnly className="common-input" />
              </Form.Item>
            </Tooltip>
          </Col>
        </Row>
        <Row className="justify-content-between">
          <Col xs={24} md={6}>
            <label>Company Name</label>
          </Col>
          <Col xs={24} md={17}>
            <Form.Item name="company" className="mb15">
              <PlaceAutocomplete
                onPlaceSelect={(v) => {
                  form.setFieldsValue({
                    company: v?.name,
                    website: v?.website,
                  });
                }}
              />
            </Form.Item>
          </Col>
        </Row>

        <div className="mt-3 mb-3 d-flex gap-20 align-items-center  justify-content-end">
          <Button onClick={onInitialValue}>Cancel</Button>

          <Button className="theme-btn" type="primary" htmlType="submit">
            Save
          </Button>
        </div>
      </Form>
    </div>
  );
};

export default Info;
